import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import Loader from "../common/Loader";
import {
  StatCard,
  ContentCard,
  DashboardHeader,
  Icons,
} from "../common/DashboardComponents";
import PolicyModal from "./PolicyModal";
import { fetchPolicies, handleDeletePolicy } from "../../redux/actions/grievanceActions1";

const PolicyList = () => {
  const dispatch = useDispatch();
  const { policies, loading } = useSelector((state) => state.grievances);

  const userData = JSON.parse(localStorage.getItem("user"));
  const [filteredPolicies, setFilteredPolicies] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [policyDetails, setPolicyDetails] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [policiesPerPage] = useState(10);

  // Text trimming length
  const MAX_TITLE_LENGTH = 50;

  // Transform API data to match component expectations - memoized to prevent infinite re-renders
  const transformedPolicies = useMemo(() => {
    return policies?.map(policy => ({
      id: policy?.POLICY_ID,
      title: policy?.TITLE,
      document: policy?.DOCUMENT || `${policy?.TITLE.toLowerCase().replace(/\s+/g, '_')}.pdf`,
      status: policy?.IS_ACTIVE === "1" ? "active" : "inactive",
      createdAt: policy?.ENTRY_DATETIME,
      updatedAt: policy?.ENTRY_DATETIME
    })) || [];
  }, [policies]);


  useEffect(() => {
    dispatch(fetchPolicies());
  }, [dispatch]);

  const handleCloseModal = async (shouldRefresh = false) => {
    setShowModal(false);
    setModalMode("add");
    
    // Refresh the list if add/update was successful
    if (shouldRefresh) {
      await dispatch(fetchPolicies());
    }
  };

  const handleUpdateModal = (policy) => {
    setModalMode("update");
    setPolicyDetails(policy);
    setShowModal(true);
  };
  
  const handleViewModal = (policy) => {
    setModalMode("view");
    setPolicyDetails(policy);
    setShowModal(true);
  };
  
  const openModal = () => {
    setModalMode("add");
    setShowModal(true);
  };

  const deletePolicy = async (id) => {
    if (
      window.confirm(
        "Are you sure you want to delete this policy? This action cannot be undone."
      )
    ) {
      const res = await dispatch(handleDeletePolicy(id));
      if (res?.rCode === 0) {
        alert(res?.rMessage || "Policy deleted successfully!");
        await dispatch(fetchPolicies());
      } else {
        alert(res?.rMessage || "Error deleting policy!");
      }
    }
  };

  useEffect(() => {
    let result = [...transformedPolicies];

    if (statusFilter !== "all") {
      result = result.filter((policy) => policy.status === statusFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (policy) =>
          (policy.title && policy.title.toLowerCase().includes(term)) ||
          (policy.document && policy.document.toLowerCase().includes(term))
      );
    }

    setFilteredPolicies(result);
    setCurrentPage(1);
  }, [transformedPolicies, searchTerm, statusFilter]);

  // Get current policies for pagination
  const indexOfLastPolicy = currentPage * policiesPerPage;
  const indexOfFirstPolicy = indexOfLastPolicy - policiesPerPage;
  const currentPolicies = filteredPolicies.slice(
    indexOfFirstPolicy,
    indexOfLastPolicy
  );
  const totalPages = Math.ceil(filteredPolicies.length / policiesPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Text trimming function
  const trimText = (text, maxLength) => {
    if (!text) return "";
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };

  // Statistics calculation
  const totalPolicies = transformedPolicies.length;
  const activePolicies = transformedPolicies.filter(policy => policy.status === "active").length;
  const inactivePolicies = transformedPolicies.filter(policy => policy.status === "inactive").length;

  if (loading && transformedPolicies.length === 0) {
    return <Loader message="Loading policies..." />;
  }

  return (
    <div className="container-fluid p-4">
      <DashboardHeader title="Policy Dashboard" />

      {/* Statistics Cards */}
      <div className="row mb-4">
        <div className="col-md-4 mb-3">
          <StatCard
            title="Total Policies"
            value={totalPolicies}
            icon={Icons.Document}
            color="#4CAF50"
          />
        </div>
        <div className="col-md-4 mb-3">
          <StatCard
            title="Active Policies"
            value={activePolicies}
            icon={Icons.CheckCircle}
            color="#2196F3"
          />
        </div>
        <div className="col-md-4 mb-3">
          <StatCard
            title="Inactive Policies"
            value={inactivePolicies}
            icon={Icons.XCircle}
            color="#FF9800"
          />
        </div>
      </div>

      {/* Filters and Search */}
      <ContentCard
        title="Search & Filters"
        icon={Icons.Filter}
        className="mb-4"
      >
        <div className="row g-3">
          <div className="col-md-8">
            <div className="input-group">
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search policies by title or document name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="col-md-4">
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Policies</option>
              <option value="active">Active Policies</option>
              <option value="inactive">Inactive Policies</option>
            </select>
          </div>
        </div>
      </ContentCard>

      {/* Policies Table/Cards */}
      <ContentCard
        title={`Policy List (${filteredPolicies.length} policies found)`}
        icon={Icons.Document}
        action={
          (userData?.role !== 4 && userData?.role !== 6 && userData?.role !== 7) && (
          <button
            className="btn btn-primary rounded-pill shadow-sm px-3 py-1"
            onClick={() => {
              openModal();
            }}
          >
            <i className="bi bi-plus-lg me-1"></i> Add New Policy
          </button>
          )
        }
      >
        {/* Mobile-friendly card view for small screens */}
        <div className="d-md-none">
          {currentPolicies.length === 0 ? (
            <div className="alert alert-info">No policies found</div>
          ) : (
            currentPolicies.map((policy) => (
              <div key={policy.id} className="card mb-3 shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h6 className="card-title mb-0 fw-bold">
                      {trimText(policy.title, MAX_TITLE_LENGTH)}
                    </h6>
                    <div className="btn-group btn-group-sm">
                      <button
                        className="btn btn-outline-success"
                        title="View"
                        onClick={() => handleViewModal(policy)}
                      >
                        <i className="bi bi-eye-fill"></i>
                      </button>
                      <button
                        className="btn btn-outline-info"
                        title="Edit"
                        onClick={() => handleUpdateModal(policy)}
                      >
                        <i className="bi bi-pencil-fill"></i>
                      </button>
                      <button
                        className="btn btn-outline-danger"
                        title="Delete"
                        onClick={() => deletePolicy(policy.id)}
                      >
                        <i className="bi bi-trash-fill"></i>
                      </button>
                    </div>
                  </div>
                  
                  <div className="row g-2 mb-2">
                    <div className="col-6">
                      <small className="text-muted d-block">
                        <i className="bi bi-file-earmark me-1"></i>
                        {policy.document}
                      </small>
                    </div>
                    <div className="col-6">
                      <small className="text-muted d-block">
                        <i className="bi bi-calendar me-1"></i>
                        {policy.createdAt}
                      </small>
                    </div>
                  </div>
                  
                  <div className="mb-2">
                    <span className={`badge ${policy.status === 'active' ? 'bg-success' : 'bg-warning'}`}>
                      <i className={`bi ${policy.status === 'active' ? 'bi-check-circle' : 'bi-x-circle'} me-1`}></i>
                      {policy.status.charAt(0).toUpperCase() + policy.status.slice(1)}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Table view for larger screens */}
        <div className="d-none d-md-block">
          <div className="table-responsive">
            <table className="table table-striped table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>Policy Title</th>
                  <th>Document</th>
                  <th>Status</th>
                  <th>Created Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentPolicies.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center">
                      No policies found
                    </td>
                  </tr>
                ) : (
                  currentPolicies.map((policy) => (
                    <tr key={policy.id}>
                      <td>
                        <strong>
                          {trimText(policy.title, MAX_TITLE_LENGTH)}
                        </strong>
                      </td>
                      <td>
                        <i className="bi bi-file-earmark me-1"></i>
                        {policy.document}
                      </td>
                      <td>
                        <span className={`badge ${policy.status === 'active' ? 'bg-success' : 'bg-warning'}`}>
                          <i className={`bi ${policy.status === 'active' ? 'bi-check-circle' : 'bi-x-circle'} me-1`}></i>
                          {policy.status.charAt(0).toUpperCase() + policy.status.slice(1)}
                        </span>
                      </td>
                      <td>{policy.createdAt}</td>
                      <td>
                        {userData?.role !== 4 && userData?.role !== 6 && userData?.role !== 7 && <button
                          className="btn btn-sm btn-outline-info me-1"
                          title="Edit"
                          onClick={() => {
                            handleUpdateModal(policy);
                          }}
                        >
                          <i className="bi bi-pencil-fill"></i>
                        </button>}

                        <button
                          className="btn btn-sm btn-outline-success me-1"
                          title="View"
                          onClick={() => {
                            handleViewModal(policy);
                          }}
                        >
                          <i className="bi bi-eye-fill"></i>
                        </button>

                        {userData?.role !== 4 && userData?.role !== 6 && userData?.role !== 7 && <button
                          className="btn btn-sm btn-outline-danger"
                          title="Delete"
                          onClick={() => {
                            deletePolicy(policy.id);
                          }}
                        >
                          <i className="bi bi-trash-fill"></i>
                        </button>}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modernized Pagination */}
        {filteredPolicies.length > 0 && (
          <div className="d-flex justify-content-between align-items-center mt-4">
            <div>
              <small className="text-muted">
                Showing {indexOfFirstPolicy + 1} to{" "}
                {Math.min(indexOfLastPolicy, filteredPolicies.length)} of{" "}
                {filteredPolicies.length} policies
              </small>
            </div>
            <nav aria-label="Policies pagination">
              <ul className="pagination pagination-md justify-content-end mb-0">
                {/* First page button */}
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link rounded-start border-0 shadow-sm"
                    onClick={() => paginate(1)}
                    aria-label="First page"
                  >
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                </li>

                {/* Previous button */}
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link border-0 shadow-sm"
                    onClick={() => paginate(currentPage - 1)}
                    aria-label="Previous page"
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>

                {/* Page buttons */}
                {[...Array(totalPages).keys()].map((number) => {
                  // Show first page, last page, and 1 page before and after current page
                  if (
                    number + 1 === 1 ||
                    number + 1 === totalPages ||
                    Math.abs(currentPage - (number + 1)) <= 1
                  ) {
                    return (
                      <li
                        key={number + 1}
                        className={`page-item ${
                          currentPage === number + 1 ? "active" : ""
                        }`}
                      >
                        <button
                          className={`page-link border-0 shadow-sm ${
                            currentPage === number + 1 ? "bg-primary" : ""
                          }`}
                          onClick={() => paginate(number + 1)}
                        >
                          {number + 1}
                        </button>
                      </li>
                    );
                  }
                  // Add ellipsis if there's a gap
                  else if (
                    (number + 1 === 2 && currentPage > 3) ||
                    (number + 1 === totalPages - 1 &&
                      currentPage < totalPages - 2)
                  ) {
                    return (
                      <li key={number + 1} className="page-item disabled">
                        <span className="page-link border-0">...</span>
                      </li>
                    );
                  }
                  return null;
                })}

                {/* Next button */}
                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link border-0 shadow-sm"
                    onClick={() => paginate(currentPage + 1)}
                    aria-label="Next page"
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>

                {/* Last page button */}
                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link rounded-end border-0 shadow-sm"
                    onClick={() => paginate(totalPages)}
                    aria-label="Last page"
                  >
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </ContentCard>
      <PolicyModal 
        onClose={handleCloseModal} 
        show={showModal} 
        mode={modalMode}
        policyDetails={policyDetails}
      />
    </div>
  );
};

export default PolicyList;
